<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FinestCard from '@/components/FinestCard.vue'
import ValueDisplay from '@/components/ValueDisplay.vue'
import EmptyState from '@/components/EmptyState.vue'
import PolicyList, { type PolicyRow } from '@/pages/banks/my-bank/PolicyList.vue'
import { financingProfile } from '@/pages/banks/my-bank/utils'
import PartnerBankData = App.Data.PartnerBank.PartnerBankData

const props = defineProps<{
  partnerBank: PartnerBankData
}>()

const { t } = useI18n()

const profile = computed(() => financingProfile(props.partnerBank))

const acceptanceRows = computed<PolicyRow[]>(() => {
  const p = profile.value
  if (!p) return []
  return [
    { label: t('accepts_mezzanine'), decision: p.accepts_mezzanine },
    { label: t('requires_spousal_obligation'), decision: p.requires_spousal_obligation },
    { label: t('accepts_ancillary_cost_financing'), decision: p.accepts_ancillary_cost_financing },
    { label: t('accepts_modernization_financing'), decision: p.accepts_modernization_financing },
    {
      label: t('accepts_object_level_undercoverage_with_portfolio_compensation'),
      decision: p.accepts_object_level_undercoverage_with_portfolio_compensation,
    },
    { label: t('accepts_replacement_collateral'), decision: p.accepts_replacement_collateral },
    {
      label: t('accepts_ktv_guarantees_as_equity_replacement'),
      decision: p.accepts_ktv_guarantees_as_equity_replacement,
    },
  ]
})

const localityRows = computed<PolicyRow[]>(() => {
  const p = profile.value
  if (!p) return []
  return [
    { label: t('locality_object_in_region'), decision: p.locality_object_in_region },
    { label: t('locality_customer_in_region'), decision: p.locality_customer_in_region },
    {
      label: t('locality_customer_anywhere_in_germany_if_object_in_region'),
      decision: p.locality_customer_anywhere_in_germany_if_object_in_region,
    },
    {
      label: t('locality_object_anywhere_in_germany_if_customer_in_region'),
      decision: p.locality_object_anywhere_in_germany_if_customer_in_region,
    },
  ]
})
</script>

<template>
  <EmptyState v-if="!profile" :description="t('no_profile')" />

  <div v-else class="flex flex-col gap-6">
    <div class="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <FinestCard :title="t('key_figures')" icon="pi pi-calculator">
        <div class="grid gap-x-12 gap-y-6 sm:grid-cols-2">
          <ValueDisplay
            :label="t('minimum_loan_amount')"
            :value="profile.minimum_loan_amount"
            format="euros"
            :maximum-fraction-digits="0" />
          <ValueDisplay
            :label="t('maximum_loan_amount')"
            :value="profile.maximum_loan_amount"
            format="euros"
            :maximum-fraction-digits="0" />
          <ValueDisplay
            :label="t('minimum_interest_term_years')"
            :value="profile.minimum_interest_term_years"
            format="number"
            :suffix="` ${t('years')}`" />
          <ValueDisplay
            :label="t('maximum_interest_term_years')"
            :value="profile.maximum_interest_term_years"
            format="number"
            :suffix="` ${t('years')}`" />
          <ValueDisplay
            :label="t('minimum_repayment_residential_percent')"
            :value="profile.minimum_repayment_residential_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('minimum_repayment_commercial_percent')"
            :value="profile.minimum_repayment_commercial_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('management_cost_residential_percent')"
            :value="profile.management_cost_residential_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('management_cost_commercial_percent')"
            :value="profile.management_cost_commercial_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('management_cost_other_percent')"
            :value="profile.management_cost_other_percent"
            format="number"
            suffix=" %" />
        </div>
      </FinestCard>

      <FinestCard :title="t('acceptances')" icon="pi pi-check-circle">
        <PolicyList :items="acceptanceRows" />
      </FinestCard>
    </div>

    <FinestCard :title="t('locality')" icon="pi pi-map">
      <PolicyList :items="localityRows" />
    </FinestCard>

    <FinestCard v-if="profile.notes" :title="t('notes')" icon="pi pi-info-circle">
      <p class="text-surface-700">{{ profile.notes }}</p>
    </FinestCard>
  </div>
</template>

<i18n>
{
  "de": {
    "no_profile": "Kein Finanzierungsprofil hinterlegt.",
    "key_figures": "Eckdaten",
    "minimum_loan_amount": "Mindestdarlehen",
    "maximum_loan_amount": "Maximaldarlehen",
    "minimum_interest_term_years": "Zinsbindung (min.)",
    "maximum_interest_term_years": "Zinsbindung (max.)",
    "years": "Jahre",
    "minimum_repayment_residential_percent": "Mindesttilgung Wohnen",
    "minimum_repayment_commercial_percent": "Mindesttilgung Gewerbe",
    "management_cost_residential_percent": "Verwaltungskosten Wohnen",
    "management_cost_commercial_percent": "Verwaltungskosten Gewerbe",
    "management_cost_other_percent": "Verwaltungskosten Sonstige",
    "acceptances": "Akzeptierte Bausteine",
    "accepts_mezzanine": "Mezzanine-Kapital",
    "requires_spousal_obligation": "Mithaftung Ehepartner erforderlich",
    "accepts_ancillary_cost_financing": "Nebenkostenfinanzierung",
    "accepts_modernization_financing": "Modernisierungsfinanzierung",
    "accepts_object_level_undercoverage_with_portfolio_compensation": "Objekt-Unterdeckung mit Portfolioausgleich",
    "accepts_replacement_collateral": "Ersatzsicherheiten",
    "accepts_ktv_guarantees_as_equity_replacement": "KTV-Bürgschaften als EK-Ersatz",
    "locality": "Regionalität",
    "locality_object_in_region": "Objekt in der Region",
    "locality_customer_in_region": "Kunde in der Region",
    "locality_customer_anywhere_in_germany_if_object_in_region": "Kunde bundesweit, wenn Objekt in Region",
    "locality_object_anywhere_in_germany_if_customer_in_region": "Objekt bundesweit, wenn Kunde in Region",
    "notes": "Hinweise"
  },
  "en": {
    "no_profile": "No financing profile set.",
    "key_figures": "Key Figures",
    "minimum_loan_amount": "Minimum Loan",
    "maximum_loan_amount": "Maximum Loan",
    "minimum_interest_term_years": "Interest Term (min.)",
    "maximum_interest_term_years": "Interest Term (max.)",
    "years": "years",
    "minimum_repayment_residential_percent": "Min. Repayment Residential",
    "minimum_repayment_commercial_percent": "Min. Repayment Commercial",
    "management_cost_residential_percent": "Management Cost Residential",
    "management_cost_commercial_percent": "Management Cost Commercial",
    "management_cost_other_percent": "Management Cost Other",
    "acceptances": "Accepted Components",
    "accepts_mezzanine": "Mezzanine Capital",
    "requires_spousal_obligation": "Spousal Obligation Required",
    "accepts_ancillary_cost_financing": "Ancillary Cost Financing",
    "accepts_modernization_financing": "Modernization Financing",
    "accepts_object_level_undercoverage_with_portfolio_compensation": "Object Undercoverage with Portfolio Compensation",
    "accepts_replacement_collateral": "Replacement Collateral",
    "accepts_ktv_guarantees_as_equity_replacement": "KTV Guarantees as Equity Replacement",
    "locality": "Locality",
    "locality_object_in_region": "Object in Region",
    "locality_customer_in_region": "Customer in Region",
    "locality_customer_anywhere_in_germany_if_object_in_region": "Customer Nationwide if Object in Region",
    "locality_object_anywhere_in_germany_if_customer_in_region": "Object Nationwide if Customer in Region",
    "notes": "Notes"
  }
}
</i18n>
